import React, { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import IconButton from '@mui/material/IconButton';

// Enable the UTC plugin
dayjs.extend(utc);

const SearchFilter = ({ fields = [], onSearch, showSearchButton = false }) => {
  const initialState = fields.reduce((acc, field) => {
    acc[field.name] = field.type === 'date'
      ? (field.value ? dayjs.utc(field.value) : null)
      : (field.value || '');
    return acc;
  }, {});
  const [formData, setFormData] = useState(initialState);

  useEffect(() => {
    setFormData((prev) => {
      const updatedFormData = { ...prev };
      fields.forEach((field) => {
        const newValue = field.type === 'date'
          ? (field.value ? dayjs.utc(field.value) : null)
          : (field.value || '');
        if (updatedFormData[field.name] !== newValue) {
          updatedFormData[field.name] = newValue;
        }
      });
      return updatedFormData;
    });
  }, [fields]);

  const handleFieldChange = (fieldName, value, field) => {
    setFormData((prevState) => ({
      ...prevState,
      [fieldName]: field.type === 'date' && value ? dayjs.utc(value) : value,
    }));
    if (field.onChange) {
      const formattedData = Object.keys(formData).reduce((acc, key) => {
        const f = fields.find((f) => f.name === key);
        acc[key] = f.type === 'date' && formData[key]
          ? dayjs.utc(formData[key]).format('YYYY-MM-DD')
          : formData[key];
        return acc;
      }, {});
      formattedData[fieldName] = field.type === 'date' && value
        ? dayjs.utc(value).format('YYYY-MM-DD')
        : value;
      field.onChange(formattedData[fieldName]);
    }
  };

  const handleSearchClick = () => {
    const formattedData = Object.keys(formData).reduce((acc, key) => {
      const field = fields.find((f) => f.name === key);
      acc[key] = field.type === 'date' && formData[key]
        ? dayjs.utc(formData[key]).format('YYYY-MM-DD')
        : formData[key];
      return acc;
    }, {});
    onSearch(formattedData);
  };

  const handleResetClick = () => {
    const resetState = fields.reduce((acc, field) => {
      acc[field.name] = field.defaultValue
        ? (field.type === 'date' ? dayjs.utc(field.defaultValue) : field.defaultValue)
        : (field.type === 'date' ? null : '');
      return acc;
    }, {});
    setFormData(resetState);
    onSearch(resetState);
    fields.forEach((field) => {
      if (field.onChange) {
        field.onChange(field.defaultValue);
      }
    });
  };

  const hasActiveFilters = Object.keys(formData).some((key) => {
    const field = fields.find((f) => f.name === key);
    return field.type === 'date' ? formData[key] !== null : formData[key] !== '';
  });

  const commonInputStyles = {
    '& .MuiInputBase-root': {
      height: '32px',
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
    },
    '& .MuiInputLabel-root': {
      fontSize: '14px',
      transform: 'translate(12px, 8px) scale(1)',
      color: '#000',
      '&.Mui-focused': {
        color: '#000',
      },
      '&.MuiInputLabel-shrink': {
        transform: 'translate(12px, -6px) scale(0.75)',
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: '6px 12px',
      fontWeight: 'normal',
    },
    '& .MuiAutocomplete-option': {
      fontSize: '14px',
      fontWeight: 'normal',
      '&[aria-selected="true"]': {
        backgroundColor: '#2196f3',
        color: '#fff',
      },
    },
    // Ensure text field width is flexible
    width: '100%',
    maxWidth: '100%',
  };

  const renderField = (field) => {
    switch (field.type) {
      case 'date':
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format="DD-MM-YYYY"
              label={field.label}
              value={formData[field.name]}
              onChange={(newValue) => handleFieldChange(field.name, newValue, field)}
              slotProps={{
                textField: {
                  variant: 'outlined',
                  size: 'small',
                  fullWidth: true,
                  sx: commonInputStyles,
                },
              }}
              timezone="UTC"
            />
          </LocalizationProvider>
        );

      case 'dropdown':
        return (
          <Autocomplete
            options={field.options || []}
            getOptionLabel={(option) => option.label || option}
            value={field.options?.find((opt) => (opt.value || opt) === formData[field.name]) || null}
            onChange={(event, newValue) =>
              handleFieldChange(field.name, newValue ? newValue.value || newValue : '', field)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.label}
                variant="outlined"
                size="small"
                sx={commonInputStyles} // Use common styles for flexible width
                InputLabelProps={{
                  style: { color: field.required ? '#f00' : '#000' },
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => (option.value || option) === (value.value || value)}
            renderOption={(props, option) => (
              <MenuItem
                {...props}
                style={{
                  fontSize: '14px',
                  backgroundColor:
                    formData[field.name] === (option.value || option) ? '#2196f3' : 'transparent',
                  color: formData[field.name] === (option.value || option) ? '#fff' : '#000',
                }}
              >
                {option.label || option}
              </MenuItem>
            )}
          />
        );

      case 'text':
      default:
        return (
          <TextField
            label={field.label}
            variant="outlined"
            value={formData[field.name] || ''}
            onChange={(event) => handleFieldChange(field.name, event.target.value, field)}
            size="small"
            sx={commonInputStyles} // Use common styles for flexible width
          />
        );
    }
  };

  if (!Array.isArray(fields) || fields.length === 0) return null;

  return (
    <div className="container-fluid">
      <div className="d-flex flex-wrap align-items-end justify-content-between gap-2">
        <div className="d-flex flex-wrap gap-2" style={{ flex: 1 }}>
          {fields.map((field) => (
            <div
              key={field.name}
              style={{
                flex: '1 1 0', // Allow fields to grow and shrink equally
                minWidth: '120px', // Reduced minWidth for better flexibility
                maxWidth: '100%', // Prevent overflow
              }}
            >
              {renderField(field)}
            </div>
          ))}
        </div>

        <div className="d-flex gap-2 flex-shrink-0">
          <IconButton onClick={handleResetClick} title="Clear Filters">
            {hasActiveFilters ? <FilterAltOffIcon /> : <FilterAltIcon />}
          </IconButton>
          {showSearchButton && (
            <button className="submit-btn" onClick={handleSearchClick}>
              Search
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchFilter;